import {
  Activity, ArrowDown, ArrowRight, ArrowUp,
  Bell, Check, Layers, Play, Users,
} from "lucide-react";
import ChartMock from "./ChartMock.tsx";
import { scrollToSection } from "../../utils/scrollToSection.ts";
import { SECTION_IDS } from "../../constants/sections.ts";
import { cn } from "../../lib/utils.ts";

const LIVE_SIGNALS = [
  { pair: "EUR/USD", type: "CALL", conf: "87%", delta: "+0.12%" },
  { pair: "GBP/USD", type: "PUT",  conf: "72%", delta: "-0.08%" },
  { pair: "BTC/USD", type: "CALL", conf: "91%", delta: "+1.24%" },
];

const FEATURES = [
  { icon: Activity, label: "Monitorização em Tempo Real" },
  { icon: Layers,   label: "Múltiplos Ativos" },
  { icon: Bell,     label: "Alertas Instantâneos" },
  { icon: Users,    label: "Comunidade Ativa" },
];

const TRUST_ITEMS = [
  "Planos a partir de $19",
  "Sinais ilimitados",
  "Cancele quando quiser",
];

const STATS = [
  { value: "24",  label: "Sinais Hoje" },
  { value: "84%", label: "Taxa IA" },
  { value: "48",  label: "Ativos" },
];

function LiveTerminal() {
  return (
    <div className="relative">
      <div className="absolute -inset-4 rounded-[32px] bg-[#14b86a]/20 blur-3xl" aria-hidden="true" />
      <div className="landing-terminal-card relative overflow-hidden rounded-[28px] bg-[#0c2e22] shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#24453b]/70 px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-[#2abd78] motion-safe:animate-pulse" />
          <span className="text-[13px] font-medium text-[#8bd8a7]">Ao Vivo</span>
        </div>
        <div className="flex items-center gap-3 text-[#75c693]">
          <Bell className="h-4 w-4" />
          <Activity className="h-4 w-4" />
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3 px-5 pt-5">
        {STATS.map(({ value, label }) => (
          <div key={label} className="rounded-xl bg-white/5 px-3 py-2.5 text-center">
            <p className="text-lg font-bold tabular-nums text-white">{value}</p>
            <p className="text-[10px] text-[#8bd8a7]/60">{label}</p>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="mx-5 mt-4 rounded-xl bg-white/5 p-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-xs font-medium text-[#8bd8a7]">EUR/USD · 5min</span>
          <span className="font-mono text-xs font-medium text-[#14b86a]">↗ +0.34%</span>
        </div>
        <div className="h-[96px]">
          <ChartMock />
        </div>
      </div>

      {/* Signal list */}
      <div className="px-5 py-4">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-[#8bd8a7]/50">
          Sinais em Tempo Real
        </p>
        <div className="flex flex-col gap-2">
          {LIVE_SIGNALS.map((signal) => (
            <div
              key={signal.pair}
              className="flex items-center justify-between rounded-xl bg-white/5 px-3 py-2.5"
            >
              <div className="flex items-center gap-2.5">
                <div
                  className={cn(
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
                    signal.type === "CALL"
                      ? "bg-[#1dbb72]/25 text-[#29d585]"
                      : "bg-[#e05c49]/25 text-[#f07161]"
                  )}
                >
                  {signal.type === "CALL"
                    ? <ArrowUp className="h-3.5 w-3.5" />
                    : <ArrowDown className="h-3.5 w-3.5" />
                  }
                </div>
                <div>
                  <p className="text-[12px] font-bold text-white">{signal.pair}</p>
                  <p className="text-[10px] text-[#5e9a75]">{signal.type}</p>
                </div>
              </div>
              <div className="text-right">
                <p className={cn(
                  "font-mono text-[13px] font-bold",
                  signal.type === "CALL" ? "text-tv-live" : "text-tv-sell"
                )}>
                  {signal.conf}
                </p>
                <p className={cn(
                  "font-mono text-[10px]",
                  signal.delta.startsWith("+") ? "text-tv-live" : "text-tv-sell"
                )}>
                  {signal.delta}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}

export default function TradingHero() {
  return (
    <div className="landing-hero-bg bg-ld-base pt-[72px]">
      <div className="mx-auto flex min-h-[828px] max-w-[1260px] items-center px-4 py-12 md:px-6 lg:py-0">
        <div className="grid w-full items-center gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">

          {/* Left column */}
          <div className="mx-auto max-w-[560px] lg:mx-0 lg:pl-3">
            {/* Badge */}
            <p className="ld-badge mb-5 inline-flex border-[#1a805f] bg-[#063d2d]/70 px-4 py-2 text-[12px] text-[#39f0c8] [&::before]:hidden">
              ◇ SINAIS DE TRADING COM IA
            </p>

            {/* Headline */}
            <h1 className="text-[44px] font-black leading-[0.98] text-white md:text-[58px] lg:text-[62px]">
              Sinais Inteligentes para
              <br />
              <span className="bg-gradient-to-br from-[#22c36f] via-[#2be39d] to-[#2fe6c3] bg-clip-text text-transparent">
                Decisões
                <br />
                Mais Rápidas
              </span>
            </h1>

            {/* Subtitle */}
            <p className="mt-7 max-w-[520px] text-[20px] leading-[1.45] text-white/[0.78]">
              Receba alertas CALL e PUT em tempo real, insights de mercado e
              análise com IA — concebidos para traders que precisam
              de clareza e disciplina.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => scrollToSection(SECTION_IDS.precos)}
                className="ld-btn-primary ld-btn-lg w-full text-[16px] sm:w-auto"
              >
                Ver Planos
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="ld-btn-secondary ld-btn-lg w-full border-[#159064]/70 bg-[#063d2d]/20 text-[16px] font-bold sm:w-auto"
                onClick={() => scrollToSection(SECTION_IDS.comoFunciona)}
              >
                <Play className="h-3.5 w-3.5 shrink-0" />
                Ver Demonstração
              </button>
            </div>

            {/* Trust bullets */}
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2">
              {TRUST_ITEMS.map((item) => (
                <span
                  key={item}
                  className="flex items-center gap-2 text-[14px] text-white/[0.62]"
                >
                  <Check className="h-4 w-4 shrink-0 text-[#29d585]" />
                  {item}
                </span>
              ))}
            </div>

            {/* Feature row */}
            <div className="mt-10 pt-2">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                {FEATURES.map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-2 text-white/[0.68]">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#14b86a]/15 text-[#28d283]">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="text-[12px] leading-tight">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right column: live terminal */}
          <div className="mx-auto w-full max-w-[670px] lg:mx-0">
            <LiveTerminal />
          </div>

        </div>
      </div>
    </div>
  );
}
